import {
  ClockCircleOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  TagsOutlined,
} from '@ant-design/icons'
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  Select,
  Space,
} from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import programApi from '../../api/program.api'
import ContentHeader from '../../components/shared/ContentHeader'
import styles from '../../styles/product.module.scss'
import '../../styles/program.scss'
import { programOption } from './model'

const ProgramRegisterPage = () => {
  const [programData, setProgramData] = useState({
    name: '',
    description: '',
    featureTypes: [],
    programType: null,
    programOptions: [
      {
        activeDay: null,
        programOptionName: '',
      },
    ],
  })
  const [programNameCnt, setProgramNameCnt] = useState(0)
  const [programDescriptionCnt, setProgramDescriptionCnt] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [programType, setProgramType] = useState([])
  const navigate = useNavigate()

  // 프로그램 정보 변경
  const handleProgramDataChange = (e) => {
    const { name, value } = e.target
    setProgramData({ ...programData, [name]: value })
  }

  // 프로그램 타입 변경 (*내부 형식 차이)
  const handleProgramSettingChange = (type, value) => {
    if (type === 'featureTypes') {
      setProgramData((prev) => ({
        ...prev,
        featureTypes: value,
      }))
    } else if (type === 'programType') {
      const selectedValue = value.target.value
      setProgramData((prev) => ({
        ...prev,
        programOptions: [
          {
            activeDay: parseInt(selectedValue),
            programOptionName: prev.programOptions[0]?.programOptionName || '',
          },
        ],
      }))
    } else if (type === 'programOptionName') {
      // 프로그램 옵션명 변경
      setProgramData((prev) => ({
        ...prev,
        programOptions: [
          {
            ...prev.programOptions[0],
            programOptionName: value,
          },
        ],
      }))
    }
  }

  const saveProgram = async () => {
    setIsLoading(true)
    try {
      const param = {
        programName: programData.name,
        programDescription: programData.description,
        featureTypes: programData.featureTypes,
        programOptions: programData.programOptions,
      }

      const res = await programApi.upsertProgram(param)
      if (res.status === 200) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: '프로그램이 성공적으로 등록되었습니다.',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        })
        navigate('/program/manage')
      }
    } catch (error) {
      console.log(error)
      const errorDescription =
        error.response.data.detail[0].description ||
        '프로그램 등록 중 오류가 발생했습니다.'
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'error',
        title: errorDescription,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const getProgramType = async () => {
    try {
      const res = await programApi.getProgramType()
      if (res.status === 200) {
        setProgramType(res.data || [])
      } else {
        setProgramType([])
      }
    } catch (error) {
      console.log('getProgramType error', error)
      setProgramType([])
    }
  }
  useEffect(() => {
    getProgramType()
  }, [])

  useEffect(() => {
    setProgramData({
      name: '',
      description: '',
      featureTypes: [],
      programType: null,
      programOptions: [
        {
          activeDay: null,
          programOptionName: '',
        },
      ],
    })
    setProgramNameCnt(0)
    setProgramDescriptionCnt(0)
    setIsLoading(false)
    setProgramType([])
  }, [])

  const disabledSaveButton = () => {
    return (
      programData.name.length === 0 ||
      programData.description.length === 0 ||
      programData.featureTypes.length === 0 ||
      programData.programOptions[0]?.activeDay === null ||
      programData.programOptions[0]?.programOptionName.length === 0
    )
  }

  const addProgramOption = () => {
    setProgramData((prev) => ({
      ...prev,
      programOptions: [
        ...prev.programOptions,
        { programOptionName: '', activeDay: null },
      ],
    }))
  }

  // 옵션 정보 변경
  const handleProgramOptionChange = (index, key, value) => {
    const updatedOptions = [...programData.programOptions]
    updatedOptions[index][key] = value
    setProgramData((prev) => ({ ...prev, programOptions: updatedOptions }))
  }

  // 옵션 삭제
  const removeProgramOption = (index) => {
    const updatedOptions = [...programData.programOptions]
    updatedOptions.splice(index, 1)
    setProgramData((prev) => ({ ...prev, programOptions: updatedOptions }))
  }

  return (
    <div style={{ width: '100%', paddingBottom: '8rem' }}>
      <ContentHeader title="프로그램 생성" />
      {/* 프로그램 정보 */}
      <Card
        title={
          <>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            프로그램 정보
          </>
        }
        style={{ marginBottom: 20 }}
      >
        <Form.Item
          className={`${['product-register-form-input-wrap']} ${['center-label']}`}
          label="프로그램명"
          labelAlign="left"
          colon={false}
          labelCol={{
            flex: '0 0 120px',
            style: {
              minWidth: 120,
              whiteSpace: 'nowrap',
              fontWeight: 500,
            },
          }}
          wrapperCol={{ flex: '1 1 auto' }}
          required
        >
          <Input
            name="name"
            value={programData.name}
            maxLength={50}
            onChange={(e) => {
              handleProgramDataChange(e)
              setProgramNameCnt(e.target.value.length)
            }}
          />
          <div className={`${['product-name-count-wrap']}`}>
            <span className={`${['product-name-count']}`}>
              {programNameCnt}
            </span>
            /50
          </div>
        </Form.Item>
        <Divider className={`${['product-register-form-input-divider']}`} />
        <Form.Item
          className={`${['product-register-form-input-wrap']} ${['center-label']}`}
          label="프로그램 설명"
          labelAlign="left"
          colon={false}
          labelCol={{
            flex: '0 0 120px',
            style: {
              minWidth: 120,
              whiteSpace: 'nowrap',
              fontWeight: 500,
            },
          }}
          wrapperCol={{ flex: '1 1 auto' }}
          required
        >
          <Input
            name="description"
            value={programData.description}
            maxLength={50}
            onChange={(e) => {
              handleProgramDataChange(e)
              setProgramDescriptionCnt(e.target.value.length)
            }}
          />
          <div className={`${['product-name-count-wrap']}`}>
            <span className={`${['product-name-count']}`}>
              {programDescriptionCnt}
            </span>
            /50
          </div>
        </Form.Item>
      </Card>

      {/* 프로그램 타입 */}
      <Card
        title={
          <>
            <TagsOutlined style={{ marginRight: 8 }} />
            프로그램 타입
          </>
        }
        style={{ marginBottom: 20 }}
      >
        <Checkbox.Group
          options={programType.map((item) => ({
            label: item,
            value: item,
          }))}
          value={programData.featureTypes}
          onChange={(value) =>
            handleProgramSettingChange('featureTypes', value)
          }
        />
      </Card>

      {/* 프로그램 옵션 */}
      <Card
        title={
          <>
            <ClockCircleOutlined style={{ marginRight: 8 }} />
            프로그램 옵션 (기간)
          </>
        }
        style={{ marginBottom: 20 }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {programData.programOptions?.map((opt, index) => (
            <Space key={opt.id ?? `new-${index}`}>
              <Input
                placeholder="옵션명 입력"
                value={opt.programOptionName || ''}
                onChange={(e) =>
                  handleProgramOptionChange(
                    index,
                    'programOptionName',
                    e.target.value,
                  )
                }
                style={{ width: '200px' }}
              />
              <Select
                placeholder="기간 선택"
                value={
                  opt.activeDay !== null && opt.activeDay !== undefined
                    ? opt.activeDay
                    : undefined
                }
                onChange={(value) =>
                  handleProgramOptionChange(index, 'activeDay', value)
                }
                style={{ width: '120px' }}
                options={programOption.map((item) => ({
                  label: item.label,
                  value: parseInt(item.type),
                }))}
                allowClear
              />
              <Button
                icon={<DeleteOutlined />}
                onClick={() => removeProgramOption(index)}
                danger
                size="small"
              />
            </Space>
          ))}
          <Button type="dashed" onClick={addProgramOption} block>
            + 옵션 추가
          </Button>
        </Space>
      </Card>

      {/* 버튼 하단 고정 */}
      <div className={`${styles['product-register-fixed-footer']}`}>
        <Button
          loading={isLoading}
          className={`${styles['product-register-button']} ${styles.save}`}
          onClick={saveProgram}
          disabled={disabledSaveButton()}
        >
          저장하기
        </Button>
      </div>
    </div>
  )
}

export default ProgramRegisterPage
